import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const todayISO = () => format(new Date(), 'yyyy-MM-dd');

const LogProductionModal = ({ rooms, activeCycles, onClose, onSuccess, harvestOnly = true, editRecord = null }) => {
  const isEdit = !!editRecord;
  const [formData, setFormData] = useState({
    harvest_date: editRecord ? (editRecord.harvest_date || '').slice(0, 10) || todayISO() : todayISO(),
    room_id: editRecord ? editRecord.room_id || '' : '',
    room_cycle_id: editRecord ? editRecord.room_cycle_id || '' : '',
    batch_name: editRecord ? editRecord.batch_name || '' : '',
    weight_kg: editRecord ? (editRecord.weight_kg ?? '') : '',
    quality_grade: editRecord ? editRecord.quality_grade || 'A' : 'A',
    notes: editRecord ? editRecord.notes || '' : ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Build the list of rooms eligible for harvest logging.
  // When `harvestOnly` is true (default), only rooms whose active cycle is in
  // Flush 1, Flush 2, or about to enter Flush 1 (Day 30+) are shown.
  // When editing, always show all rooms (the record may be for any room).
  const harvestRooms = useMemo(() => {
    if (!harvestOnly || isEdit) return rooms;
    const isHarvestPhase = (phase) => {
      const n = (phase || '').toLowerCase().replace(/\s+/g, '_');
      return n === 'flush_1' || n === 'flush_2';
    };
    const eligibleRoomIds = new Set(
      activeCycles
        .filter((c) => isHarvestPhase(c.current_phase) || (c.current_day || 0) >= 30)
        .map((c) => c.room_id)
    );
    return rooms.filter((r) => eligibleRoomIds.has(r.id));
  }, [rooms, activeCycles, harvestOnly, isEdit]);

  const handleRoomChange = (roomId) => {
    const cycle = activeCycles.find((c) => c.room_id === roomId);
    setFormData((prev) => ({
      ...prev,
      room_id: roomId,
      room_cycle_id: cycle ? cycle.id : '',
      batch_name: cycle ? cycle.batch_name || '' : ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.room_id || !formData.weight_kg) {
      toast.error('Please select a room and enter weight');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        room_id: formData.room_id,
        room_cycle_id: formData.room_cycle_id || null,
        batch_name: formData.batch_name || null,
        weight_kg: parseFloat(formData.weight_kg),
        quality_grade: formData.quality_grade,
        harvest_date: formData.harvest_date,
        notes: formData.notes || null
      };

      if (isEdit) {
        await axios.put(`${API}/production/${editRecord.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Production record updated');
      } else {
        await axios.post(`${API}/production`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Production logged successfully');
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isEdit ? 'update' : 'log'} production`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="log-production-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Production Record' : 'Log Actual Production'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="harvest_date">Date</Label>
              <Input
                id="harvest_date"
                type="date"
                value={formData.harvest_date}
                onChange={(e) => setFormData({ ...formData, harvest_date: e.target.value })}
                required
                className="mt-1"
                data-testid="prod-date-input"
              />
            </div>
            <div>
              <Label htmlFor="quality_grade">Grade</Label>
              <select
                id="quality_grade"
                value={formData.quality_grade}
                onChange={(e) => setFormData({ ...formData, quality_grade: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg bg-white"
                data-testid="prod-grade-select"
              >
                <option value="A">Grade A (Premium)</option>
                <option value="B">Grade B (Standard)</option>
                <option value="C">Grade C (Commercial)</option>
              </select>
            </div>
          </div>

          <div>
            <Label htmlFor="room_id">Room</Label>
            <select
              id="room_id"
              value={formData.room_id}
              onChange={(e) => handleRoomChange(e.target.value)}
              required
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg bg-white"
              data-testid="prod-room-select"
            >
              <option value="">Select a room</option>
              {harvestRooms.length === 0 && (
                <option value="" disabled>
                  No rooms in harvest phase right now
                </option>
              )}
              {harvestRooms.map((room) => {
                const cycle = activeCycles.find((c) => c.room_id === room.id);
                const phaseLabel = cycle
                  ? `${cycle.batch_name} — ${cycle.current_phase || 'Active'} (Day ${cycle.current_day})`
                  : '';
                return (
                  <option key={room.id} value={room.id}>
                    {room.name}{phaseLabel ? ` — ${phaseLabel}` : ''}
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <Label htmlFor="batch_name">Batch</Label>
            <Input
              id="batch_name"
              type="text"
              value={formData.batch_name}
              onChange={(e) => setFormData({ ...formData, batch_name: e.target.value })}
              placeholder="Auto-filled from active cycle"
              className="mt-1"
              data-testid="prod-batch-input"
            />
          </div>

          <div>
            <Label htmlFor="weight_kg">Quantity (kg)</Label>
            <Input
              id="weight_kg"
              type="number"
              step="0.01"
              min="0"
              placeholder="Enter weight in kg"
              value={formData.weight_kg}
              onChange={(e) => setFormData({ ...formData, weight_kg: e.target.value })}
              required
              className="mt-1"
              data-testid="prod-weight-input"
            />
          </div>

          <div>
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Flush, observations, etc."
              rows={2}
              className="mt-1"
              data-testid="prod-notes-input"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-[#1A4D2E] hover:bg-[#143D23] text-white"
              data-testid="submit-log-production"
            >
              {submitting ? 'Saving...' : (isEdit ? 'Save Changes' : 'Save Production')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LogProductionModal;
